import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from 'react-toastify';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import MessageCard from '../../../components/message/MessageCard';
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { Message as MessageModel } from '../../../models/Message';
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { getMessageById, getMessageNamesWithPagination, storeMessage } from '../../../services/messageService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/configuration/messages';
const itemPath = '/access-control/configuration/message/';

const initialItem: MessageModel = {
    id: 0,
    name: '',
    site: '',
    siteId: '',
    default: undefined
}

const Message: FC = () => {
    const { translate } = useLocale();
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<MessageModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const reloadPage = async () => {
        try {
            let message;

            if (itemId) {
                message = await getMessageById(itemId);

                setSelectedItem({label: message.name, value: message.id.toString(), isDefault: message.default})
                setCurrentItem(message);
            }
            else {
                setCurrentItem(initialItem)
            }

            setBreadcrumb([
                { value: '/access-control/configuration', displayName: translate.byKey(TranslationKeys.configuration), disableClick: true },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.messages) },
                { value: itemPath + `${itemId}`, displayName: `${message?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const topic: Topic = {
        accessor: 'lmMessage',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.messages),
    }

    const saveMessage = (values) => {
        const newMessage = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
        }
        const isNew = itemId ? +itemId < 0 : true;
        saveItem(setBusy, storeMessage, newMessage, isNew, setItemId, history)
    }

    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(item);
                toast.success(translate.byKey(TranslationKeys.messageSaved));;
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    return <TopicContextProvider dataMethod={getMessageNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={() => { }}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveMessage}
                tabs={[]}
                itemPath={itemPath}
                initialValues={currentItem}
                children={
                    <MessageCard />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Message;
